import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Shield, Trash2, Calendar, User, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { http } from '@/libs/http';
import { useLayout } from '@/contexts/layout-context';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/loader';

interface RoleAssignmentDetails {
    id: string;
    role_id: string;
    principal_id: string;
    principal_type: 'user' | 'group' | 'spn';
    scope_id: string;
    assigned_at?: string;
    assigned_by?: string;
}

const PRINCIPAL_TYPE_LABELS: Record<string, string> = {
    user: 'User',
    group: 'Group',
    spn: 'Service Principal',
};

export const ManageAssignmentPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();
    const [assignment, setAssignment] = useState<RoleAssignmentDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [revoking, setRevoking] = useState(false);

    useEffect(() => {
        setBreadcrumbs([
            { name: 'Assignments', to: '/m/assignments' },
            { name: 'Manage Assignment' },
        ]);
    }, [setBreadcrumbs]);

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        http.get<RoleAssignmentDetails>(`/mgmt/v1/assignments/roles/${id}`)
            .then(data => {
                setAssignment(data);
            })
            .catch(err => {
                console.error(err);
                toast.error('Failed to load assignment details');
            })
            .finally(() => setLoading(false));
    }, [id]);

    const handleRevoke = async () => {
        if (!assignment) return;
        if (!confirm('Are you sure you want to revoke this role assignment? This action cannot be undone.')) return;
        
        setRevoking(true);
        try {
            await http.delete(`/mgmt/v1/assignments/roles/${assignment.id}`);
            toast.success('Role assignment revoked successfully');
            navigate('/m/assignments');
        } catch (err: any) {
            console.error(err);
            toast.error(err.message || 'Failed to revoke assignment');
        } finally {
            setRevoking(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <Loader />
            </div>
        );
    }

    if (!assignment) {
        return (
            <div className="p-8 max-w-xl mx-auto text-center space-y-4">
                <div className="w-16 h-16 bg-red-50 dark:bg-red-500/10 rounded-full flex items-center justify-center mx-auto text-red-500">
                    <AlertTriangle className="w-8 h-8" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Assignment Not Found</h2>
                <p className="text-slate-500 dark:text-slate-400">The requested role assignment could not be found or has been revoked.</p>
                <Button onClick={() => navigate('/m/assignments')}>Back to Assignments</Button>
            </div>
        );
    }

    return (
        <div className="p-4 lg:p-8 max-w-2xl mx-auto w-full animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex items-center gap-3 mb-8">
                <button
                    onClick={() => navigate('/m/assignments')}
                    className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-[--primary]" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-slate-900 dark:text-white">Manage Assignment</h1>
                        <p className="text-sm text-slate-500 dark:text-slate-400">View details and revoke access</p>
                    </div>
                </div>
            </div>

            {/* Content Card */}
            <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-900 p-6 space-y-6">
                <div className="border-b border-slate-100 dark:border-slate-800 pb-6">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center shrink-0">
                            <Shield className="w-6 h-6 text-[--primary]" />
                        </div>
                        <div>
                            <span className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Role Assigned</span>
                            <h2 className="text-lg font-bold text-slate-900 dark:text-white font-mono mt-0.5">{assignment.role_id}</h2>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1">
                        <div className="flex items-center gap-2 text-slate-400 dark:text-slate-500 text-xs font-medium">
                            <User className="w-3.5 h-3.5" />
                            <span>Principal Type</span>
                        </div>
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                            {PRINCIPAL_TYPE_LABELS[assignment.principal_type] || assignment.principal_type}
                        </p>
                    </div>

                    <div className="space-y-1">
                        <div className="flex items-center gap-2 text-slate-400 dark:text-slate-500 text-xs font-medium">
                            <FileText className="w-3.5 h-3.5" />
                            <span>Principal ID</span>
                        </div>
                        <p className="text-sm font-mono text-slate-800 dark:text-slate-200 break-all select-all">
                            {assignment.principal_id}
                        </p>
                    </div>

                    <div className="space-y-1">
                        <div className="flex items-center gap-2 text-slate-400 dark:text-slate-500 text-xs font-medium">
                            <Shield className="w-3.5 h-3.5" />
                            <span>Scope</span>
                        </div>
                        <p className="text-sm font-mono text-slate-800 dark:text-slate-200 break-all">
                            {assignment.scope_id}
                        </p>
                    </div>

                    <div className="space-y-1">
                        <div className="flex items-center gap-2 text-slate-400 dark:text-slate-500 text-xs font-medium">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>Assigned At</span>
                        </div>
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                            {assignment.assigned_at ? new Date(assignment.assigned_at).toLocaleString() : '—'}
                        </p>
                    </div>
                </div>

                <div className="border-t border-slate-100 dark:border-slate-800 pt-6 flex items-center justify-between gap-4">
                    <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Revoke Role Assignment</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Immediately remove this principal's access to the resource scope.</p>
                    </div>
                    <Button
                        variant="danger"
                        onClick={handleRevoke}
                        disabled={revoking}
                        className="gap-2 shrink-0"
                    >
                        <Trash2 className="w-4 h-4" />
                        {revoking ? 'Revoking...' : 'Revoke'}
                    </Button>
                </div>
            </div>
        </div>
    );
};
